import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type ReportMode = "conservative" | "balanced" | "detailed";

const STORAGE_KEY = "rl:portal:report-mode";

const MODE_DETAILS: Record<ReportMode, { title: string; description: string }> = {
  conservative: {
    title: "Conservative",
    description: "Shortest clinically complete output with minimal additional prose.",
  },
  balanced: {
    title: "Balanced",
    description: "Default mode balancing brevity and completeness for routine workflow.",
  },
  detailed: {
    title: "Detailed",
    description: "More explanatory phrasing and richer context where clinically relevant.",
  },
};

export function PortalReportPreferences() {
  const [mode, setMode] = useState<ReportMode>("balanced");
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY) as ReportMode | null;
    if (raw && raw in MODE_DETAILS) setMode(raw);
  }, []);

  const saveMode = () => {
    localStorage.setItem(STORAGE_KEY, mode);
    setSaved("Saved preference");
    window.setTimeout(() => setSaved(null), 1600);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Report Preferences</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Choose your report-building mode. Raw temperature/model controls are intentionally hidden.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(Object.keys(MODE_DETAILS) as ReportMode[]).map((key) => {
          const selected = key === mode;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className={`rounded-lg border p-4 text-left transition-colors ${
                selected ? "border-primary bg-primary/5" : "border-border/50 hover:bg-accent/30"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">{MODE_DETAILS[key].title}</div>
                {selected && <Badge variant="default">Selected</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">{MODE_DETAILS[key].description}</p>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-border/50 bg-background/60 p-3 text-sm text-muted-foreground">
        Mode policies are mapped to controlled prompt templates and guardrails to preserve radiology report quality.
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={saveMode}>Save Preferences</Button>
        {saved && <span className="text-xs text-emerald-600">{saved}</span>}
      </div>
    </div>
  );
}
